import { useMutation } from '@tanstack/react-query';
import { Button, Label, Modal, type ModalProps,Textarea } from 'flowbite-react';
import { CheckIcon, LifeBuoyIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';

interface FormValues {
    message: string;
}

export interface DocumentHelpModalProps extends ModalProps {
    documentId: string;
    documentSection?: string|null;
    fields?: Record<string, string>;
}

export const DocumentHelpModal = ({
    documentId,
    documentSection,
    fields,
    show,
    onClose
}: DocumentHelpModalProps) => {
    const api = useApi();

    const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
        defaultValues: {
            message: ''
        }
    });

    const submitFeedback = useMutation({
        mutationFn: (data: FormValues) => api.v1.productSupport.submitFeedback({
            category: 'Pomoc s obsahem',
            documentId,
            documentSection: documentSection ?? null,
            message: data.message,
            fields: fields ?? {},
            locationInApp: 'Editace dokumentu'
        })
    });

    const onSubmit = async (data: FormValues) => {
        submitFeedback.mutate(data);
    };

    return (
        <Modal show={show} size="lg" onClose={onClose} popup>
            <Modal.Header>
                <span className="flex flex-row items-start md:items-center leading-5 justify-center gap-2 p-3 text-base md:text-lg font-semibold text-gray-800 dark:text-gray-400">
                    <LifeBuoyIcon size={20} className='flex-shrink-0' />
                    <span>Chci pomoci s obsahem mé práce</span>
                </span>
            </Modal.Header>
            <Modal.Body>
                {!submitFeedback.isSuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
                        <ul className='flex flex-col items-start justify-start gap-2 empty:hidden'>
                            {documentSection && (
                                <li className='text-sm'>
                                    <span className='block md:inline-block md:w-[10rem] font-semibold mr-1'>Sekce dokumentu:</span>
                                    <span>{documentSection}</span>
                                </li>
                            )}

                            {fields && Object.keys(fields).length > 0 && (
                                <>
                                    {Object.entries(fields).map(([key, value]) => (
                                        <li key={key} className='text-sm'>
                                            <span className='block md:inline-block md:w-[10rem] font-semibold mr-1'>{key}:</span>
                                            <span>{value}</span>
                                        </li>
                                    ))}
                                </>
                            )}
                        </ul>

                        <div>
                            <Label htmlFor="message" className="block mb-1">S čím potřebujete pomoci?</Label>
                            <Textarea
                                id="message"
                                {...register('message', {
                                    required: 'Musíte vyplnit zprávu.',
                                    maxLength: { value: 30000, message: 'Maximální délka je 30 000 znaků' }
                                })}
                                rows={10}
                                color={errors.message ? 'failure' : 'default'}
                                helperText={(
                                    <>
                                        {errors.message && (
                                            <span className='block'>
                                                {errors.message.message}
                                            </span>
                                        )}
                                    </>
                                )}
                                disabled={submitFeedback.isPending || submitFeedback.isSuccess}
                            />
                        </div>
                    
                        {!submitFeedback.isSuccess && (
                            <div className='flex flex-row items-center justify-end gap-2'>
                                <ErrorSwitch
                                    error={submitFeedback.error}
                                    default={{ message: 'Při odesílání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                                    options={[]}
                                    className='grow'
                                />

                                <Button
                                    type='submit'
                                    color='primary'
                                    disabled={submitFeedback.isPending}
                                >Odeslat zprávu</Button>

                                <Button
                                    color='gray'
                                    onClick={onClose}
                                >Zavřít</Button>
                            </div>
                        )}
                    </form>
                )}

                {submitFeedback.isSuccess && (
                    <div className='flex flex-col gap-4'>
                        <div className='flex flex-row items-center justify-center gap-2 p-4 border border-gray-300 rounded-lg'>
                            <CheckIcon size={32} className='text-lime-600' />
                            <p className='font-medium text-lime-700'>Vaše zpráva byla odeslána. Budeme Vás co nejdříve kontaktovat zpět.</p>
                        </div>

                        <div className='flex flex-row items-center justify-center gap-2 flex-wrap'>
                            <Button
                                color='primary'
                                onClick={onClose}
                            >Zavřít</Button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};
